// Les confettis du mode fun, en un seul endroit : une rafale part d'ici ou ne part pas.
//
// Trois intensités grossières (`spark`, `burst`, `party`) plutôt qu'un nombre de particules à
// choisir sur chaque appel. C'est ce qui garantit qu'un succès anodin ne se fête jamais comme
// un contrat signé — et qu'un futur point de branchement n'aura pas à réinventer le dosage.
//
// Trois garde-fous, parce que des confettis non bridés rendent une application inutilisable :
//
//   - le mode doit être actif ET les confettis non coupés dans les réglages ;
//   - le mouvement réduit coupe tout ;
//   - un étranglement global : une rafale toutes les 1,8 s au maximum, TOUS appelants
//     confondus. Une page qui enchaîne trois succès Turbo n'en tire qu'une.
//
// `js-confetti` est importé DYNAMIQUEMENT : hors mode fun personne n'appelle `celebrate`, donc
// personne ne télécharge la bibliothèque.

const LEVELS = {
  spark: { confettiNumber: 14, emojiSize: 26 },
  burst: { confettiNumber: 45, emojiSize: 34 },
  party: { confettiNumber: 120, emojiSize: 44 }
}

const EMOJIS = ["🎉", "✨", "🥳", "🎊", "⭐", "💫", "🙌", "🔥", "💜", "🎈", "🏆", "👏"]

// Les skins à décor fêtent avec le leur. Des emojis anciens (Unicode 12 au plus) : un confetti ne
// doit jamais tomber en carré vide sur un système un peu daté.
const SKIN_EMOJIS = {
  ocean: ["🐠", "🐟", "🐡", "🐙", "🐚", "🦀", "🐬", "🐳", "⭐", "💧"],
  desert: ["🌵", "🤠", "⭐", "🐎", "☀️", "🏜️", "💰", "🌞"],
  sky: ["☁️", "🎈", "🕊️", "🌈", "☀️", "🪁", "✈️", "⭐", "🌤️"],
  depozemon: ["⭐", "🏅", "🎒", "🥚", "🌱", "🔥", "💧", "✨", "📜"]
}

const THROTTLE_MS = 1800
const COMBO_WINDOW_MS = 12000
const COMBO_FROM = 3
const COMBO_VISIBLE_MS = 1600

let enginePromise = null
let lastAt = 0
let combo = 0
let comboTimer = null
let comboHideTimer = null

function reducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
}

// Lu sur le body à CHAQUE appel et pas mis en cache : Turbo remplace le body à chaque visite,
// et le joueur peut couper les confettis entre deux.
function enabled() {
  const body = document.body
  return Boolean(body?.classList.contains("fun-mode")) && body.dataset.funConfetti !== "off"
}

function engine() {
  if (!enginePromise) {
    enginePromise = import("js-confetti")
      .then((module) => new module.default())
      .catch((error) => {
        // Une seule plainte : on remet la promesse à zéro pour ne pas figer un échec réseau
        // passager, mais on ne réessaie pas tout seul.
        enginePromise = null
        throw error
      })
  }
  return enginePromise
}

function shuffled(list) {
  return [...list].sort(() => Math.random() - 0.5)
}

/**
 * Fête quelque chose, si le moment s'y prête.
 *
 * @param {"spark"|"burst"|"party"} level
 * @returns {Promise<boolean>} true si une rafale est effectivement partie
 */
export async function celebrate(level = "spark") {
  if (!enabled() || reducedMotion()) return false

  const now = Date.now()
  if (now - lastAt < THROTTLE_MS) return false
  lastAt = now

  try {
    const confetti = await engine()
    const emojis = SKIN_EMOJIS[document.body?.dataset.funSkin] || EMOJIS
    confetti.addConfetti({ ...(LEVELS[level] || LEVELS.spark), emojis: shuffled(emojis) })
  } catch (error) {
    console.warn("[fun] confettis indisponibles", error)
    return false
  }

  bumpCombo()
  return true
}

// La troisième fête rapprochée déclenche un compteur de combo. C'est le seul endroit du mode
// qui récompense l'enchaînement : une bonne série de saisies devient un petit score.
function bumpCombo() {
  combo += 1
  clearTimeout(comboTimer)
  comboTimer = setTimeout(() => { combo = 0 }, COMBO_WINDOW_MS)

  if (combo < COMBO_FROM) return
  showCombo(combo)
}

function showCombo(count) {
  let badge = document.querySelector(".fun-combo")
  if (!badge) {
    badge = document.createElement("div")
    badge.className = "fun-combo"
    badge.setAttribute("aria-hidden", "true")
    document.body.appendChild(badge)
  }

  badge.textContent = `×${count} 🔥`
  clearTimeout(comboHideTimer)
  comboHideTimer = setTimeout(() => badge.remove(), COMBO_VISIBLE_MS)
}

// Pour les tests et pour la sortie du mode : un compteur de combo qui survivrait au
// reverrouillage serait un fantôme.
export function resetCelebrations() {
  lastAt = 0
  combo = 0
  clearTimeout(comboTimer)
  clearTimeout(comboHideTimer)
  document.querySelector(".fun-combo")?.remove()
}

// --- l'étincelle du clic ------------------------------------------------------------------

// Un clic de bouton n'est pas un succès : c'est une intention. Il ne passe donc PAS par
// `celebrate` et n'entre pas dans le combo — sans quoi deux allers-retours dans un formulaire
// afficheraient « ×7 🔥 » et mangeraient l'étranglement réservé aux vraies fêtes.
//
// Pas `js-confetti` non plus, pour deux raisons : la bibliothèque ne sait tirer que depuis le
// centre de l'écran (une gerbe plein cadre à chaque clic serait intenable), et on ne charge pas
// 12 ko au premier bouton pressé. Six pastilles en DOM pur, au point exact du clic, qui se
// suppriment toutes seules.
//
// Étranglement propre, bien plus court que celui des fêtes : il n'est là que pour le clic
// répété (une pagination qu'on martèle), pas pour espacer les événements.

const SPARK_COUNT = 6
const SPARK_THROTTLE_MS = 220
const SPARK_LIFE_MS = 700
const SPARK_COLORS = ["#7c3aed", "#f59e0b", "#ec4899", "#10b981", "#3b82f6"]

let lastSparkAt = 0
let sparkLayer = null

function layer() {
  // Recréée si Turbo a remplacé le body sous nos pieds : `isConnected` est le seul test fiable.
  if (!sparkLayer?.isConnected) {
    sparkLayer = document.createElement("div")
    sparkLayer.className = "fun-sparks"
    sparkLayer.setAttribute("aria-hidden", "true")
    document.body.appendChild(sparkLayer)
  }
  return sparkLayer
}

/**
 * Une poignée de pastilles qui s'échappent d'un point. Silencieuse hors mode fun.
 *
 * @param {number} x coordonnées viewport (clientX / clientY d'un événement de souris)
 * @param {number} y
 * @returns {boolean} true si quelque chose a été rendu
 */
export function sparkleAt(x, y) {
  if (!enabled() || reducedMotion()) return false

  const now = Date.now()
  if (now - lastSparkAt < SPARK_THROTTLE_MS) return false
  lastSparkAt = now

  const host = layer()

  for (let i = 0; i < SPARK_COUNT; i += 1) {
    const spark = document.createElement("span")
    spark.className = "fun-spark"

    // Réparties sur le cercle avec un peu de jeu : un éventail parfaitement régulier se voit,
    // et se voit mal.
    const angle = (i / SPARK_COUNT) * Math.PI * 2 + Math.random() * 0.6
    const distance = 18 + Math.random() * 20

    spark.style.left = `${x}px`
    spark.style.top = `${y}px`
    spark.style.setProperty("--fun-spark-dx", `${Math.cos(angle) * distance}px`)
    spark.style.setProperty("--fun-spark-dy", `${Math.sin(angle) * distance}px`)
    spark.style.setProperty("--fun-spark-color", SPARK_COLORS[i % SPARK_COLORS.length])
    spark.style.animationDelay = `${Math.random() * 60}ms`

    // Ceinture et bretelles : `animationend` nettoie normalement, le timer couvre le cas où
    // l'animation ne démarre jamais (onglet en arrière-plan au moment du clic).
    spark.addEventListener("animationend", () => spark.remove(), { once: true })
    setTimeout(() => spark.remove(), SPARK_LIFE_MS + 200)

    host.appendChild(spark)
  }

  return true
}

export function resetSparkles() {
  lastSparkAt = 0
  sparkLayer?.remove()
  sparkLayer = null
}
